import React from 'react'
import PropTypes from 'prop-types'
import {
  DialogWindow,
  DialogHeader,
  DialogAdornment,
  useDialog,
  ContentTransition,
  DialogBody,
} from '@ticketswap/solar'
import { ChevronLeftAlt, CloseAlt } from '@ticketswap/comets'
import { withApollo } from '@apollo/react-hoc'
import { withTranslation } from '~/i18n'
import {
  Login,
  SignupSocial,
  LoginLinkSent,
  SignupEmail,
} from '~/components/AuthenticationPageContent'
import { createMarkup } from '~/utils'

const AuthDialog = ({ t, client, children, ...props }) => {
  const [title, setTitle] = React.useState('')
  const [back, setBack] = React.useState('')
  const initialView = 'login'
  const [activeView, setActiveView] = React.useState(initialView)
  const { show, hide, getToggleProps, getWindowProps } = useDialog({
    onToggle: on => {
      props.onToggle(on)
      if (on) {
        // Select initial view upon opening dialog
        setActiveView(initialView)
      }
    },
  })

  return (
    <>
      {children({ show, getToggleProps })}
      <DialogWindow
        {...getWindowProps()}
        onClick={e => {
          e.preventDefault()
          hide()
        }}
      >
        <DialogHeader>
          {back && (
            <DialogAdornment left>
              <button onClick={() => setActiveView(back)}>
                <ChevronLeftAlt size={24} />
              </button>
            </DialogAdornment>
          )}
          {title}
          <DialogAdornment right>
            <button onClick={hide}>
              <CloseAlt size={24} />
            </button>
          </DialogAdornment>
        </DialogHeader>
        <ContentTransition
          onChange={({ props }) => {
            props.title && setTitle(props.title)
            props.back && setBack(props.back)
          }}
          activeView={activeView}
        >
          <DialogBody key="login" title={t('auth.login.title')}>
            <Login
              t={t}
              redirectUrl={props.emailLoginRedirectUrl}
              onEmailLogin={() => setActiveView('loginLinkSent')}
              onEmailSignup={() => setActiveView('signupEmail')}
              onSocialLogin={() => {
                hide()
                props.onComplete()
              }}
              onSocialSignup={({ firstName }) => {
                setActiveView('signupSocial')
                // `setTitle()` needs to be called at the end, otherwise the
                // `onChange` handler of the <ContentTransition /> component
                // would reset it.
                setTitle(
                  <span
                    dangerouslySetInnerHTML={createMarkup(
                      t('generic.hi', {
                        firstname: firstName,
                      }),
                      {
                        renderInline: true,
                      }
                    )}
                  />
                )
              }}
            />
          </DialogBody>
          <DialogBody
            key="loginLinkSent"
            title={t('auth.loginLinkSent.title')}
            back="login"
          >
            <LoginLinkSent t={t} />
          </DialogBody>
          <DialogBody
            key="signupEmail"
            title={t('auth.signupEmail.title')}
            back="login"
          >
            <SignupEmail
              t={t}
              onSignupComplete={() => {
                hide()
                props.onComplete()
              }}
            />
          </DialogBody>
          <DialogBody key="signupSocial" back="login">
            <SignupSocial
              t={t}
              onSignupComplete={() => {
                hide()
                props.onComplete()
              }}
              hide={hide}
            />
          </DialogBody>
        </ContentTransition>
      </DialogWindow>
    </>
  )
}

AuthDialog.defaultProps = {
  onComplete: () => {},
}

AuthDialog.propTypes = {
  onToggle: PropTypes.func.isRequired,
}

export default withTranslation('common')(withApollo(AuthDialog))
